import { useState, useContext, useCallback, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import MapNewUserModal from "./MapNewUserModal";
import VmModal from "./VmListModal";
import UploadTemplateModal from "./UploadTemplateModal";
import UploadIcon from '../../../../assets/icons/upload.svg';
import WhiteUploadIcon from '../../../../assets/icons/WhiteUploadIcon.svg';
import DownloadIcon from '../../../../assets/icons/download.svg';
import WhiteDownloadIcon from '../../../../assets/icons/WhiteDownloadIcon.svg';
import { FaEdit, FaLaptop, FaRegTrashAlt } from "react-icons/fa";
import AuthContext from "../../../../auth/AuthContext";
import CourseService from "../../../../services/CourseService";
import AlertService from "../../../../services/AlertService";
import { useLocation } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { InputText } from "primereact/inputtext";

const UserVMs = () => {
  const authContext = useContext(AuthContext);
  // const { courseName } = useParams();
  const { state } = useLocation();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showMapUserModal, setShowMapUserModal] = useState(false);
  const [showVmModal, setShowVmModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [userVMs, setUserVMs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserVMId, setSelectedUserVMId] = useState(null);
  const [editUserVM, setEditUserVM] = useState(null);

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
    const [globalFilter, setGlobalFilter] = useState("");
  const [allUserVMs, setAllUserVMs] = useState([]);

  useEffect(() => {
    if (state?.id) authContext.courseId = state.id;
  }, [state, authContext]);

  useEffect(() => {
    const courseAccess = authContext.getPermissions("courses");
    if (!courseAccess.some(f => f.access.includes(1))) {
      window.location.href = "/";
    }
  }, [authContext]);

  const loadUserVMs = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await CourseService.GetUserVMs(
        authContext.courseId,
        authContext,
        signal
      );

      if (signal?.aborted) return; 

     if (data.isSuccess) {
  setUserVMs(data.data || []);
  setAllUserVMs(data.data || []);
} else AlertService.error(data.errorMessage);
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authContext]);

const loadUsers = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await CourseService.GetCourseUsers(
        authContext.courseId,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) setUsers(data.data || []);
      else AlertService.error(data.errorMessage);
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController();
    loadUserVMs(controller.signal);
    loadUsers(controller.signal);
    return () => controller.abort();
  }, [loadUserVMs, loadUsers]);

const handleDeleteUserVM = async () => {
  if (!deleteId) return;
  setLoading(true);
  const result = await CourseService.RemoveUserVM(deleteId, authContext);
  setShowDeleteDialog(false);

  if (result.isSuccess) {
    AlertService.success("User details deleted successfully!");

    const totalAfterDelete = userVMs.length - 1;
    const totalPages = Math.ceil(totalAfterDelete / rowsPerPage);
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    } else if (totalPages === 0) {
      setCurrentPage(0);
    }

    await loadUserVMs();
  } else {
    AlertService.error(result.errorMessage);
  }
  setLoading(false);
};

  const handleOpenVmModal = (userVMId) => {
    setSelectedUserVMId(userVMId);
    setShowVmModal(true);
  };
    const onGlobalFilterChange = (e) => {
  const value = e.target.value.toLowerCase();
  setGlobalFilter(value);

  if (value.trim() === "") {
    setUserVMs(allUserVMs);
  } else {
    const filtered = allUserVMs.filter((v) =>
      (v.displayName?.toLowerCase() || "").includes(value) ||
      (v.userName?.toLowerCase() || "").includes(value)
    );
    setUserVMs(filtered);
  }
  setCurrentPage(0);
};

  const paginatedClients = userVMs.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  const handleEditClick = (rowData) => {
    setEditUserVM(rowData);        
    setShowMapUserModal(true);
  };

  const refreshUserVMs = useCallback(() => {
  loadUserVMs();
}, [loadUserVMs]);


  const actionTemplate = (rowData) => (
    <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
     <FaLaptop
        size={22}
        style={{ cursor: "pointer" }}
        onClick={() => handleOpenVmModal(rowData.id)}
        title="View VMs"
      />
      <FaEdit
        size={21}
        className="text-success"
        style={{ cursor: "pointer" }}
        onClick={() => handleEditClick(rowData)}
        title="Edit User"
      />
      <FaRegTrashAlt
        className="text-danger"
        size={20}
        style={{ cursor: "pointer" }}
        onClick={() => {
          setDeleteId(rowData.id);
          setShowDeleteDialog(true);
        }}
        title="Delete"
      />
    </div>
  );

  const generatePageNumbers = () => {
    const totalPages = Math.ceil(userVMs.length / rowsPerPage);
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPageNumber > maxVisiblePages + 1) {
        pages.push('...');
      }

      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);

      if (currentPageNumber <= maxVisiblePages) {
        end = 4;
      } else if (currentPageNumber > totalPages - maxVisiblePages) {
        start = totalPages - 3;
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPageNumber < totalPages - maxVisiblePages) {
        pages.push('...');
      }
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="container-fluid custom-container position-relative mt-3 mb-3">
    <div className="d-flex flex-wrap gap-3 mb-4 justify-content-between align-items-center">
       <div className="global-search-wrapper">
    <i className="pi pi-search search-icon-left"></i>

    <InputText
      value={globalFilter}
      onChange={onGlobalFilterChange}
      placeholder="Search User"
      className="global-search-input text-primary"
    />

    {globalFilter && (
      <i
        className="pi pi-times search-icon-right"
        onClick={() => {
          setGlobalFilter("");
          setUserVMs(allUserVMs);
          setCurrentPage(0);
        }}
      ></i>
    )}
  </div>
    <div className="d-flex flex-wrap gap-3">
        <button className="btn border-0 text-primary" onClick={() => setShowUploadModal(true)}>
          <img src={isDark ? WhiteUploadIcon : UploadIcon} alt="Upload" className="me-2 mb-1" /> Upload Template
        </button>
          <a
          href="assets/Import VM Template.xlsx"
          download
          className="btn border-0 d-flex align-items-center text-primary"
          title="Download Excel Template"
        >
          <img src={isDark ? WhiteDownloadIcon : DownloadIcon} alt="download" className="me-2 mb-1" />
          Download Template
        </a>
        <button onClick={() => { setEditUserVM(null); setShowMapUserModal(true); }} className="btn btn-primary">
          Map New User
        </button>
        </div>
      </div>

 <DataTable
        value={loading ? Array.from({ length: rowsPerPage }).map((_, i) => ({ id: `skeleton-${i}` })) : paginatedClients}
        dataKey="id"
        className="custom-table"
        responsiveLayout="scroll"
      >
       <Column header="S.No." body={(rowData, { rowIndex }) => loading ? <Skeleton width="2rem" height='2rem' /> : rowIndex + 1 + currentPage * rowsPerPage} style={{ width: '70px' }} className="text-center" />
        <Column
          field="displayName"
          header="User"
          body={rowData => loading ? <Skeleton width="10rem"  height='2rem'/> : rowData.displayName}
          sortable
        />
        <Column
          field="userName"
          header="Username"
          body={rowData => loading ? <Skeleton width="10rem"  height='2rem'/> : rowData.userName}
          sortable
        />
        <Column
          field="password"
          header="Password"
          body={rowData => loading ? <Skeleton width="10rem"  height='2rem' /> : rowData.password}
        />
        <Column
          field="sessionTokenId"
          header="Token ID"
          body={rowData => loading ? <Skeleton width="15rem"  height='2rem' /> : rowData.sessionTokenId}
          bodyClassName="text-break"
        />
        <Column
          field="sessionToken"
          header="Token"
          body={rowData => loading ? <Skeleton width="20rem"  height='2rem' /> : rowData.sessionToken}
          bodyClassName="text-break"
        />
        <Column
          field="allottedHours"
          header="Allotted Time"
          bodyClassName="text-center"
          body={rowData => loading ? <Skeleton width="5rem"  height='2rem'/> : rowData.allottedHours}
        />
        <Column
          header="Action"
          bodyClassName="text-center"
          body={rowData => loading ? <Skeleton width="5rem" height='2rem' /> : actionTemplate(rowData)}
          style={{ width: '140px' }} 
        />
      </DataTable>
  <div className="d-flex flex-column flex-start flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
        <div className="d-flex align-items-center">
          <label className="me-2">Show Rows:</label>
          <select
            className="form-select form-select-sm w-auto py-2 text-secondary custom-input-color"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setCurrentPage(0);
            }}
          >
            {[10, 20, 50,100].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
        <div className="d-flex justify-content-center align-items-center gap-2">
          <button
            className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
            style={{ width: "40px", height: "40px" }}
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
          >
            &lt;
          </button>
          {generatePageNumbers().map((page, index) => (
            page === '...' ? (
              <span
                key={index}
                className="mx-1 text-secondary"
              >
                ...
              </span>
            ) : (
              <button
                key={index}
                className={`btn p-0 d-flex align-items-center justify-content-center text-secondary pagination-item ${currentPage + 1 === page
                  ? 'active-page'
                  : ''
                  }`}
                style={{
                  width: "40px",
                  height: "40px",
                  transition: "all 0.2s"
                }}
                onClick={() => setCurrentPage(page - 1)}
              >
                {page}
              </button>
            )
          ))}
          <button
            className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
            style={{ width: "40px", height: "40px" }}
            onClick={() => setCurrentPage(Math.min(
              Math.ceil(userVMs.length / rowsPerPage) - 1,
              currentPage + 1
            ))}
            disabled={currentPage >= Math.ceil(userVMs.length / rowsPerPage) - 1}
          >
            &gt;
          </button>
        </div>
      </div>
       <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteUserVM}
        title={<>Are you sure you want to delete this user VM?</>}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />

      <VmModal show={showVmModal} handleClose={() => setShowVmModal(false)} userVMId={selectedUserVMId} authContext={authContext} />

      <MapNewUserModal
        show={showMapUserModal}
        handleClose={() => {
          setShowMapUserModal(false);
          setEditUserVM(null);       
        }}
        users={users}
        authContext={authContext}
        onSaved={loadUserVMs}
        editData={editUserVM}        
      />

      <UploadTemplateModal show={showUploadModal} handleClose={() => setShowUploadModal(false)} courseId={state?.id} onSuccess={refreshUserVMs} />
    </div>
  );
};

export default UserVMs;
